import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import { canSeeWsApprovalsFor, fetchWsApprovalsQueue } from "./work-security-approvals.slice";
import WsApprovalsTable from "./WsApprovalsTable";

/**
 * Tasks → «Tvarkos tvirtinimai»: every pending approval the current user may
 * decide, across all their spheres. Its data lives in the work-security-approvals
 * slice.
 */
export default function WsApprovalsQueue() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authReducer.role);
  const {
    companyId: sliceCompanyId,
    rows: sliceRows,
    rowsLoaded,
    isLoading,
    isError,
  } = useAppSelector((state) => state.wsApprovalsReducer);

  const canSee = canSeeWsApprovalsFor(role, companyId);
  // Never show the previous company's rows while this one's fetch runs.
  const rows = sliceCompanyId === companyId && rowsLoaded ? sliceRows : [];

  const reload = useCallback(() => {
    if (companyId && canSee) dispatch(fetchWsApprovalsQueue(companyId));
  }, [companyId, canSee, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!companyId) return null;

  const areaCount = new Set(rows.map((row) => row.area?.id || "")).size;

  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-gray-500">{t("ws_approvals_intro")}</p>
      <WsApprovalsTable
        companyId={companyId}
        rows={rows}
        loading={isLoading || (!rowsLoaded && !isError)}
        error={isError}
        onRetry={reload}
        showArea={areaCount > 1}
      />
    </div>
  );
}
